import {
    Component, Mixins,
} from 'vue-property-decorator';
import { ValidationProvider, localize } from 'vee-validate';
import fa from 'vee-validate/dist/locale/fa.json';
import en from 'vee-validate/dist/locale/en.json';
import { digits } from '@groot-nodes/helpers/number';
import config from '@groot-components/shared/config';
import Classes from '@groot-components/shared/mixins/classes';
import Filters from '@groot-components/shared/mixins/filters';
import { snakeToCamel } from '@groot-nodes/helpers/string';
import Props from './props';
import '@groot-components/shared/mixins/forms/validation';

/**
 * @name FieldWrapper
 */
@Component({
    components: {
        ValidationProvider,
    },
})
class YFieldWrapper extends Mixins(Props, Classes, Filters) {
    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = ['size'];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.fieldWrapper);

    /**
     * @inheritdoc
     */
    created() {
        // Init Validation Locale
        if (this.$i18n.locale === 'en') {
            localize('en', en);
        } else {
            localize('fa', fa);
        }
    }

    /**
     * Create Common Props for the fieldWrapper.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            commonProps[item] = this[item];
        });
        return commonProps;
    }

    /**
     * Create classes array for the fieldWrapper.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('FieldWrapper', this.commonProps, this.baseClass || config.prefix.fieldWrapper));
        return cls;
    }

    /**
     * Return rules for validation
     * 
     * @returns {string}
     */
    get validationRules(): string {
        let rules = null;
        if (!this.getBoolean('disabled') && !this.getBoolean('readonly')) {
            rules = this.getValue('validation');
        }
        return rules;
    }

    /**
     * Return the value of the prop
     * 
     * @param {string} prop
     * @param {string} defaultValue
     * @returns {any}
     */
    getValue(prop: string, defaultValue: string | boolean = null): any {
        if (!prop) {
            return false;
        }
        if (this.props.elements && this.props.elements[prop]) {
            return this.props.elements[prop] || defaultValue;
        }
        if (this.props && this.props[prop]) {
            return this.props[prop] || defaultValue;
        }
        return this.props[snakeToCamel(prop)] || defaultValue;
    }

    /**
     * Get field value as boolean
     *
     * @param {string} prop
     * @param {boolean} default_value
     * @returns {boolean}
     */
    getBoolean(prop: string, default_value: boolean = false): boolean {
        return !!this.getValue(prop, default_value);
    }

    /**
     * Check if label should be shown
     *
     * @returns {boolean}
     */
    get shouldShowLabel(): boolean {
        return (this.getValue('label') || this.label)
                    && !this.getBoolean('no_label')
                    && this.labelFor !== null
                    && !this.noLabel;
    }
}

export default YFieldWrapper;
